// PlayerControls.tsx - Play/pause button and progress bar
import { FlexBox } from '@/styles/Boxes';
import { Icon } from '@/styles/Icon';
import React from 'react';
import { styled } from 'styled-components';
import { calculateTimeFromPosition, formatTime } from './playerUtils';

interface PlayerControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  prevButton?: React.ReactNode;
  nextButton?: React.ReactNode;
}

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  onTogglePlay,
  onSeek,
  prevButton,
  nextButton,
}) => {
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const newTime = calculateTimeFromPosition(e.clientX, rect, duration);
    onSeek(newTime);
  };

  const handleProgressDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newTime = calculateTimeFromPosition(moveEvent.clientX, rect, duration);
      onSeek(newTime);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 키보드 접근성: 진행 바 화살표 키 처리
  const handleProgressKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onSeek(Math.max(0, currentTime - 5)); // 5초 뒤로
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onSeek(Math.min(duration, currentTime + 5)); // 5초 앞으로
    }
  };

  return (
    <Container>
      <FlexBox>
        {/* 이전곡 버튼 */}
        {prevButton}

        {/* 재생/일시정지 버튼 */}
        <PlayButton onClick={onTogglePlay} aria-label={isPlaying ? '일시정지' : '재생'}>
          {isPlaying ? <Icon name="pauseBlack" size={26} /> : <Icon name="playBlack" size={26} />}
        </PlayButton>

        {/* 다음곡 버튼 */}
        {nextButton}
      </FlexBox>

      {/* 재생 시간 및 프로그레스 바 */}
      <ProgressSection>
          <ProgressBarContainer
            onClick={handleProgressClick}
            onMouseDown={handleProgressDrag}
            onKeyDown={handleProgressKeyDown}
            tabIndex={0}
            role="slider"
            aria-label="재생 위치 조절"
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={currentTime}
            aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
          >
            <ProgressBar>
              <ProgressFill $width={progressPercent} />
              <ProgressHandle $left={progressPercent} />
            </ProgressBar>
          </ProgressBarContainer>

          <FlexBox style={{ justifyContent: 'space-between'}}>
            <TimeDisplay aria-label="현재 재생 시간">{formatTime(currentTime)}</TimeDisplay>
            <TimeDisplay aria-label="전체 재생 시간">{formatTime(duration)}</TimeDisplay>
          </FlexBox>
      </ProgressSection>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 26px;
  flex: 1;
  max-width: 900px;
  margin: 0 20px;
`;

const PlayButton = styled.button`
  width: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 6px;
  flex: 1;
`;

const TimeDisplay = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.fontGray[200]};
  min-width: 35px;
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  height: 4px;
  background: ${({ theme }) => theme.colors.bgGray[600]};
  border-radius: 9999px;
`;

const ProgressFill = styled.div<{ $width: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 9999px;
  transition: width 0.1s ease;
`;

const ProgressHandle = styled.div<{ $left: number }>`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  transition: left 0.1s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }
`;
